import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "../../lib/auth";
import { Shield, Lock, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { formatApiErrorDetail } from "../../lib/api";

export default function AdminLogin() {
  const { user, loading, login, verifyTwoFactor } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  // Two-step 2FA state. `sessionToken` non-null → we're on the code-entry
  // step. `code` is either a 6-digit TOTP or a hyphenated recovery code.
  const [sessionToken, setSessionToken] = useState(null);
  const [code, setCode] = useState("");
  const nav = useNavigate();

  if (loading) return <main className="py-24 text-center text-[#447694]">Loading…</main>;
  if (user) return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const result = await login(email, password);
      if (result?.two_factor_required) {
        // Password OK - switch UI to the code-entry step. Do NOT navigate.
        setSessionToken(result.session_token);
        return;
      }
      toast.success("Welcome back.");
      nav("/admin");
    } catch (err) {
      const msg = formatApiErrorDetail(err?.response?.data?.detail) || "Login failed.";
      setError(msg);
    } finally {
      setBusy(false);
    }
  };

  const submitTwoFactor = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await verifyTwoFactor(sessionToken, code);
      toast.success("Welcome back.");
      nav("/admin");
    } catch (err) {
      const msg = formatApiErrorDetail(err?.response?.data?.detail) || "Verification failed.";
      setError(msg);
      // On any failure we keep the user on the 2FA step so they can retry
      // without re-entering their password. A too-old session token surfaces
      // as "Session expired" - surfaced verbatim from the backend detail.
    } finally {
      setBusy(false);
    }
  };

  const cancelTwoFactor = () => {
    setSessionToken(null);
    setCode("");
    setError("");
  };

  return (
    <main className="min-h-[70vh] py-20 flex items-center justify-center" data-testid="admin-login-page">
      <div className="bnp-card w-full max-w-md p-8 md:p-10">
        <div className="flex items-center gap-2.5">
          <div className="h-10 w-10 rounded-xl bg-[#04273d] flex items-center justify-center">
            <Shield size={18} className="text-[#71edc9]" />
          </div>
          <div>
            <p className="font-display font-bold text-[#04273d]">BNP Admin</p>
            <p className="font-mono-brand text-[10px] text-[#447694] tracking-widest uppercase">NBR Group</p>
          </div>
        </div>

        {!sessionToken ? (
          <>
            <h1 className="font-display text-2xl text-[#04273d] mt-6 font-semibold">Sign in</h1>
            <p className="text-sm text-[#447694] mt-1">Restricted area. Authorised personnel only.</p>

            <form onSubmit={submit} className="mt-6 space-y-4" data-testid="admin-login-form">
              <div>
                <label className="block text-xs font-mono-brand tracking-wider uppercase text-[#447694] mb-1.5">Email</label>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="bnp-input"
                  autoComplete="username"
                  data-testid="admin-login-email-input"
                />
              </div>
              <div>
                <label className="block text-xs font-mono-brand tracking-wider uppercase text-[#447694] mb-1.5">Password</label>
                <input
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="bnp-input"
                  autoComplete="current-password"
                  data-testid="admin-login-password-input"
                />
              </div>
              {error && <p className="text-sm text-red-700" data-testid="admin-login-error">{error}</p>}
              <button type="submit" disabled={busy} className="btn-primary w-full justify-center" data-testid="admin-login-submit-button">
                <Lock size={14} /> {busy ? "Signing in…" : "Sign in"}
              </button>
            </form>
          </>
        ) : (
          <>
            <h1 className="font-display text-2xl text-[#04273d] mt-6 font-semibold" data-testid="admin-2fa-heading">
              Two-factor code
            </h1>
            <p className="text-sm text-[#447694] mt-1">
              Enter the 6-digit code from your authenticator app.
            </p>
            <p className="text-xs text-[#447694] mt-1">
              Lost your device? Use one of your 10-character recovery codes instead (e.g. <span className="font-mono-brand">AAAA-BBBB-CC</span>).
            </p>

            <form onSubmit={submitTwoFactor} className="mt-6 space-y-4" data-testid="admin-2fa-form">
              <div>
                <label className="block text-xs font-mono-brand tracking-wider uppercase text-[#447694] mb-1.5">
                  Authenticator code or recovery code
                </label>
                <input
                  type="text"
                  required
                  autoFocus
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  className="bnp-input font-mono-brand tracking-[0.3em] text-center text-lg"
                  placeholder="123 456"
                  autoComplete="one-time-code"
                  inputMode="numeric"
                  data-testid="admin-2fa-code-input"
                />
              </div>
              {error && <p className="text-sm text-red-700" data-testid="admin-2fa-error">{error}</p>}
              <button type="submit" disabled={busy || code.length < 6} className="btn-primary w-full justify-center" data-testid="admin-2fa-submit-button">
                <KeyRound size={14} /> {busy ? "Verifying…" : "Verify and continue"}
              </button>
              <button
                type="button"
                onClick={cancelTwoFactor}
                className="w-full text-center text-xs text-[#447694] hover:text-[#04273d] font-mono-brand tracking-widest uppercase"
                data-testid="admin-2fa-cancel-button"
              >
                ← Back to sign in
              </button>
            </form>
          </>
        )}

        <Link to="/" className="block text-center text-xs text-[#447694] hover:text-[#04273d] mt-6 font-mono-brand tracking-widest uppercase">← Back to site</Link>
      </div>
    </main>
  );
}
